import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const products = [
  {
    id: '1',
    name: 'Classic L-Shape Couch',
    category: 'l-shape',
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/c93i9oc7_WhatsApp%20Image%202026-02-08%20at%2016.14.58.jpeg',
    price: 'R8,000 - R15,000'
  },
  {
    id: '2',
    name: 'Leather Brown Set',
    category: 'leather',
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/44vd5t34_lst7.jpeg',
    price: 'R12,000 - R20,000'
  },
  {
    id: '3',
    name: 'Modern Recliner Set',
    category: 'recliner',
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/3s6svqv6_lst8.jpeg',
    price: 'R10,000 - R18,000'
  },
  {
    id: '4',
    name: 'Velvet Corner Unit',
    category: 'corner',
    image: 'https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/wfnsvrjo_lst9.jpeg',
    price: 'R15,000 - R25,000'
  },
  {
    id: '5',
    name: 'Premium Fabric Sofa',
    category: 'fabric',
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80',
    price: 'R6,000 - R12,000'
  },
  {
    id: '6',
    name: 'Executive Office Suite',
    category: 'leather',
    image: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=600&q=80',
    price: 'R18,000 - R30,000'
  }
];

const categories = [
  { id: 'all', name: 'All' },
  { id: 'l-shape', name: 'L-Shape' },
  { id: 'corner', name: 'Corner Units' },
  { id: 'recliner', name: 'Recliners' },
  { id: 'leather', name: 'Leather' },
  { id: 'fabric', name: 'Fabric' }
];

export const ProductGallery = ({ showAll = false }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const displayProducts = showAll ? filteredProducts : filteredProducts.slice(0, 4);

  return (
    <section data-testid="product-gallery" className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-gold font-body text-sm uppercase tracking-widest">Our Collection</span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-leather mt-2 mb-4">
            Featured Couches
          </h2>
          <p className="text-leather/60 font-body max-w-2xl mx-auto">
            Browse our handcrafted collection of premium couches, each designed with comfort and style in mind.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`filter-${cat.id}`}
              className={`px-5 py-2 rounded-full font-body text-sm transition-all ${
                activeCategory === cat.id
                  ? 'bg-leather text-white'
                  : 'bg-secondary text-leather hover:bg-leather/10'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {displayProducts.map((product) => (
              <motion.div
                key={product.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="product-card group"
              >
                <div 
                  data-testid={`product-${product.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-card cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform">
                        <i className="fas fa-search-plus text-leather"></i>
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <h3 className="font-heading text-lg text-leather font-medium mb-1">
                      {product.name}
                    </h3>
                    <p className="text-gold font-body font-semibold mb-4">
                      {product.price}
                    </p>
                    <Link to="/quote" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        data-testid={`quote-btn-${product.id}`}
                        className="w-full btn-leather rounded-full text-sm"
                      >
                        Request Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* View All Button */}
        {!showAll && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/gallery">
              <Button 
                data-testid="view-all-products-btn"
                variant="outline"
                className="rounded-full px-10 py-4 text-base border-leather text-leather hover:bg-leather hover:text-white"
              >
                View All Products
                <i className="fas fa-arrow-right ml-3"></i>
              </Button>
            </Link>
          </motion.div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-overlay"
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedProduct(null)}
                data-testid="lightbox-close"
                className="absolute -top-12 right-0 text-white hover:text-gold text-2xl z-10"
              >
                <i className="fas fa-times"></i>
              </button>
              
              <div className="bg-white rounded-2xl overflow-hidden">
                <TransformWrapper>
                  <TransformComponent>
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-full max-h-[60vh] object-contain"
                    />
                  </TransformComponent>
                </TransformWrapper>
                
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-leather font-semibold mb-2">
                    {selectedProduct.name}
                  </h3>
                  <p className="text-gold font-body text-xl font-bold mb-4">
                    {selectedProduct.price}
                  </p>
                  <div className="flex gap-4">
                    <Link to="/quote" className="flex-1">
                      <Button className="w-full btn-gold rounded-full">
                        Request Quote
                      </Button>
                    </Link>
                    <a 
                      href={`https://wa.me/27752781104?text=Hi! I'm interested in the ${selectedProduct.name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white">
                        <i className="fab fa-whatsapp mr-2"></i>
                        WhatsApp
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProductGallery;
